import { useEffect, useMemo } from 'react';
import { Collapse, ToggleButtonGroup } from '@mui/material';
import { FormProvider, useForm, DefaultValues } from 'react-hook-form';
import { FlexColumn } from '@elementary/common-ui/components';
import {
  ActivityLogsIntegration,
  ActivityLogsIntegrationType,
} from 'shared/types';
import { IntegrationError } from 'settings/types';
import DialogToggleButton from 'settings/components/shared/DialogToggleButton';
import IntegrationDialog from 'settings/components/shared/Integration/IntegrationDialog';
import {
  ActivityLogsIntegrationFormValues,
  ActivityLogsIntegrationFormContent,
  getActivityLogsIntegrationDefaultValues,
  getActivityLogsIntegrationIcon,
  getActivityLogsIntegrationLabel,
} from './activityLogsIntegrationUtils';
import LogsIntegrationScopeField from './LogsIntegrationScopeField';

type Props = {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: ActivityLogsIntegrationFormValues) => void;
  supportedIntegrationTypes: ActivityLogsIntegrationType[];
  error?: IntegrationError;
  isLoading?: boolean;
  data?: ActivityLogsIntegration;
};

const ActivityLogsIntegrationDialog = ({
  open,
  onClose,
  onSubmit,
  supportedIntegrationTypes,
  error,
  isLoading,
  data,
}: Props) => {
  const isEdit = !!data?.id;

  const defaultValues = useMemo<
    DefaultValues<ActivityLogsIntegrationFormValues>
  >(() => {
    const integrationType: ActivityLogsIntegrationType =
      (data?.type as ActivityLogsIntegrationType) ||
      (supportedIntegrationTypes[0] as ActivityLogsIntegrationType);
    return getActivityLogsIntegrationDefaultValues({
      data: isEdit ? data : undefined,
      preferredType: integrationType,
    });
  }, [data, isEdit, supportedIntegrationTypes]);

  const methods = useForm<ActivityLogsIntegrationFormValues>({
    defaultValues,
    mode: 'onChange',
  });

  const {
    handleSubmit,
    formState: { isValid },
    setValue,
    reset,
    watch,
  } = methods;

  useEffect(() => {
    if (open) {
      reset(defaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const selectedType = watch('integration_type');
  const selectedIntegrationType =
    selectedType ||
    (supportedIntegrationTypes[0] as ActivityLogsIntegrationType);

  return (
    <IntegrationDialog
      open={open}
      onClose={onClose}
      title="Connect external integration"
      description="Send activity logs to an external system."
      isValid={isValid}
      reset={reset}
      onSubmit={handleSubmit(onSubmit)}
      saveButtonText={isEdit ? 'Save' : 'Connect'}
      error={error}
      loading={isLoading}
    >
      <FormProvider {...methods}>
        <ToggleButtonGroup
          value={watch('integration_type')}
          onChange={(_, val) => {
            setValue('integration_type', val);
          }}
          fullWidth
          exclusive
        >
          {supportedIntegrationTypes.map((integrationType) => (
            <DialogToggleButton key={integrationType} value={integrationType}>
              {getActivityLogsIntegrationIcon(integrationType)}
              {getActivityLogsIntegrationLabel(integrationType)}
            </DialogToggleButton>
          ))}
        </ToggleButtonGroup>

        {/* Scope is collected at the wrapper level so every integration type
            (and any future type) gets the same selector without per-form
            wiring. */}
        <LogsIntegrationScopeField />

        <Collapse in={!!watch('integration_type')}>
          <FlexColumn sx={{ gap: 3 }}>
            <ActivityLogsIntegrationFormContent
              type={selectedIntegrationType}
            />
          </FlexColumn>
        </Collapse>
      </FormProvider>
    </IntegrationDialog>
  );
};

export default ActivityLogsIntegrationDialog;
